import { useCallback, useContext, useEffect, useMemo, useState } from "react";


// Types
import { InstanceOptions, StatisticsResultsTableProps } from "./types";
import { OracleSqlHashTimeSlice, OracleSqlHashWithMetric } from "../../statistics/types";

// Context
import { InstanceContext } from "../../../context/InstanceContext";

// Helpers
import useChartOptions from "../../../helpers/useChartOptions";
import TimeSeriesChart, { TimeSeriesChartProps } from "../../../component/chart/echarts/TimeSeriesChart";
import Helper from "../../../helpers/Helper";
import {
    fetchResults,
    formatDateForApi, getAggregateInt, getTimeZone, stringFormatting,
} from "../../../helpers/utils";
import SqlApi from "../../../api/instance/SqlApi";
import { showError } from '../../../utils/showAlert';
import ConditionalRender from "../../../helpers/ConditionalRender";

// Constants
import { CHART_DATA_TYPE } from "../../../component/Constants";

const UPMOST_CHART_INDEX_VALUE = 99999

const StatisticsComparisonChart = (props: StatisticsResultsTableProps) => {
    const { instances } = useContext(InstanceContext);

    const [loading, setLoading] = useState(false)
    // const [chartOptions, setChartOptions] = useState<any[]>([{title: {text: ''}}, {title: {text: ''}}])
    const [topNChartData, setTopNChartData] = useState<{
        [instanceIndex: string | number]: { [key: string]: { values: OracleSqlHashTimeSlice[], orderIndex: number } }
    }>({});

    const timezone = getTimeZone()

    const chartMetric = useMemo(() => {
        return props.options.metric
    }, [props.generateId])

    const aggregateInt = useMemo(() => getAggregateInt(props.options.aggregate, props.instanceOptions, instances), [props.options.aggregate])

    const __getOtherTotal = (allMetrics: any, topNMetrics: any): OracleSqlHashTimeSlice => {
        let totalValue = allMetrics[props.options.metric]
        topNMetrics.forEach((item: any) => {
            const getTopNItemAtTimeSlice = item.find((x: OracleSqlHashTimeSlice) => x.timeslice === allMetrics.timeslice) || 0
            totalValue = getTopNItemAtTimeSlice[props.options.metric] ? totalValue - getTopNItemAtTimeSlice[props.options.metric] : totalValue
        })
        return {
            timeslice: allMetrics.timeslice,
            [props.options.metric]: totalValue < 0 ? 0 : totalValue
        }
    }

    const getInstanceResults = useCallback(async (instanceOption: InstanceOptions) => {
        try {
            let chartApiDataResults: OracleSqlHashWithMetric[][] = [[]]
            const from = formatDateForApi(instanceOption.periodStart, timezone)
            const to = formatDateForApi(instanceOption.periodEnd, timezone)
            let customIdApiString = ''
            if (props.options.customSqlId) {
                customIdApiString = props.options.showSqlId ? `&sqlid=${props.options.customSqlId}` : `&sqlhash=${props.options.customSqlId}`
            }
            chartApiDataResults = await fetchResults([SqlApi.fetchStatisticSql(`from=${from}&to=${to}&tz=${timezone}`, instanceOption.instanceId, instanceOption.instanceType || '', `&interval=${aggregateInt}&sort=${props.options.metric}+desc${customIdApiString}`)])

            let topNChartApiDataResults: OracleSqlHashWithMetric[] = []
            topNChartApiDataResults = chartApiDataResults[0].slice(0, props.options.topN)

            let topNChartTimePromises = []
            for (let sqlHashData of topNChartApiDataResults) {
                const apiSqlHash = `sqlhash=${sqlHashData.sqlhash}`
                topNChartTimePromises.push(SqlApi.fetchStatisticTime(`from=${from}&to=${to}&tz=${timezone}`, instanceOption.instanceId, instanceOption.instanceType || '', `&interval=${aggregateInt}&sort=${props.options.metric}+desc&${apiSqlHash}`))
            }

            const topNChartTimeResults = await fetchResults(topNChartTimePromises)

            // sort by time
            topNChartTimeResults.forEach(chartTime => {
                chartTime.sort(function compare(a: OracleSqlHashTimeSlice, b: OracleSqlHashTimeSlice) {
                    const aa: number = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
                    const bb: number = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
                    return aa < bb ? -1 : 1;
                })
            })

            let allChartTimeResults: OracleSqlHashTimeSlice[][] = []

            if (props.options.other) {
                allChartTimeResults = await fetchResults([SqlApi.fetchStatisticTime(`from=${from}&to=${to}&tz=${timezone}`, instanceOption.instanceId, instanceOption.instanceType || '', `&interval=${aggregateInt}&sort=${props.options.metric}+desc&limit=50000`)])

                // sort by time
                allChartTimeResults[0].sort(function compare(a: any, b: any) {
                    const aa: number = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
                    const bb: number = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
                    return aa - bb;
                })
            }

            const topNData: { [key: string]: { values: OracleSqlHashTimeSlice[], orderIndex: number } } = {}

            topNChartTimeResults.forEach((result: OracleSqlHashTimeSlice[], index: number) => {
                const sqlhash = props.options.showSqlId ? topNChartApiDataResults[index].sqlid : topNChartApiDataResults[index].sqlhash

                if (sqlhash) {
                    topNData[sqlhash] = {
                        values: result.map((item: any) => {
                            return {
                                timeslice: item.timeslice,
                                [props.options.metric]: item[props.options.metric],
                            }
                        }),
                        orderIndex: index + 1
                    }
                }
                if (props.options.other) {
                    const otherValues = result.map((item: OracleSqlHashTimeSlice) => {
                        const getAllAtTimeSlice = allChartTimeResults[0].find((otherItem: OracleSqlHashTimeSlice) => otherItem.timeslice === item.timeslice)
                        if (getAllAtTimeSlice) {
                            const otherTotal = __getOtherTotal(getAllAtTimeSlice, topNChartTimeResults)
                            const valuesExist = topNData['Other SQL']?.values?.find(item => item.timeslice === otherTotal.timeslice)
                            // return topNData['Other SQL']?.values.find(item => item.timeslice === otherTotal.timeslice) ? [] : otherTotal
                            return valuesExist || otherTotal
                        }
                        return []
                    })
                    topNData['Other SQL'] = {
                        // @ts-ignore
                        // values:  topNData['Other SQL']?.values ? topNData['Other SQL']?.values.concat(otherValues) : otherValues,
                        values: topNData['Other SQL']?.values || otherValues,
                        orderIndex: UPMOST_CHART_INDEX_VALUE
                    }
                }
            })

            return topNData
        } catch (error: any) {
            console.error(error)
            const errorMessage = error.message || error.toString();
            showError('Failed to load statistics comparison chart', errorMessage);
        }
    }, [topNChartData, props.generateId])

    const isTime = props.options.metric.includes('time') || props.options.metric === 'duration'

    const chartOptions = useChartOptions(
            topNChartData,
            props.instanceOptions,
            {
                metric: props.options.metric,
                tooltipType: isTime ? CHART_DATA_TYPE.TIME : CHART_DATA_TYPE.GENERIC,
                interval: aggregateInt,
                chart: props.options.chart,
                height: 500,
            },
            props.generateId,
            props.setStatementColors
    );


    useEffect(() => {
        const start = async () => {
            try {
                setLoading(true)
                const promiseArray: any[] = []
                const dataToSet: any = {}
                props.instanceOptions.forEach((instanceOption: InstanceOptions) => {
                    promiseArray.push(getInstanceResults(instanceOption))
                })

                const results = await Promise.all(promiseArray)

                results.forEach((result, index: number) => {
                    dataToSet[index] = result || []
                })

                setTopNChartData(dataToSet)
                setLoading(false)
            } catch (error: any) {
                console.error('Failed to start statistics comparison chart:', error);
                const errorMessage = error.message || error.toString();
                showError('Failed to load statistics comparison chart data', errorMessage);
                setLoading(false);
                setTopNChartData({});
            }
        }
        void start()
    }, [props.generateId])

    return <div className="card">
        <div className="card-header">
            <ConditionalRender if={loading}>
                <div className="loader"
                     style={{ borderWidth: 0, position: "absolute", width: '80%', margin: 'auto', height: "5px" }}>
                    <div className="bar"/>
                </div>
            </ConditionalRender>
            <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
            Statements {`By ${stringFormatting(chartMetric).replace(/_/gi, ' ')}`}
            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
               data-bs-target="#collapseStatementsChart" aria-expanded="false"
               aria-controls="collapseStatementsChart"/>
        </div>
        <div id="collapseStatementsChart" className="card-body collapse show combined-charts">
            <ConditionalRender if={chartOptions[0]}>
                <TimeSeriesChart {...(chartOptions[0] as TimeSeriesChartProps)}/>
            </ConditionalRender>
            <div className={"separator"}/>
            <ConditionalRender if={chartOptions[1]}>
                <TimeSeriesChart {...(chartOptions[1] as TimeSeriesChartProps)}/>
            </ConditionalRender>
        </div>
    </div>
}

export default StatisticsComparisonChart;
